<template>
  <div style="border-radius: 0.625rem; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08); border: 1px solid #E5E7EB;">

    <!-- Foundation header -->
    <div style="display: flex; align-items: center; padding: 1.125rem 1.25rem; gap: 1rem; background: white; border-left: 4px solid #EC4899;">

      <!-- Icon -->
      <div style="flex-shrink: 0; width: 3rem; height: 3rem; border-radius: 0.75rem; background: linear-gradient(135deg, #EC4899, #DB2777); display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(236, 72, 153, 0.3);">
        <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.5rem; height: 1.5rem; color: white;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      </div>

      <!-- Info -->
      <div style="flex: 1; min-width: 0;">
        <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
          <a :href="'/campaigns/' + foundation.id" target="_blank" style="font-size: 1.125rem; font-weight: 700; color: #111827; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#111827'">
            {{ foundation.name }}
          </a>
          <span style="font-size: 0.6875rem; padding: 0.1875rem 0.625rem; background: linear-gradient(135deg, #FDF2F8, #FCE7F3); color: #BE185D; border-radius: 9999px; font-weight: 600; border: 1px solid #FBCFE8;">
            {{ foundation.direct_child_count }} campaign{{ foundation.direct_child_count !== 1 ? 's' : '' }}
          </span>
          <span v-if="foundation.isPledgeDrive" style="font-size: 0.6875rem; padding: 0.1875rem 0.5rem; background: #EFF6FF; color: #1D4ED8; border-radius: 9999px; font-weight: 600; border: 1px solid #BFDBFE;">
            Pledge Drive
          </span>
        </div>
        <!-- Progress bar -->
        <div style="margin-top: 0.5rem; display: flex; align-items: center; gap: 0.5rem;">
          <div style="flex: 1; height: 0.375rem; background: #F3F4F6; border-radius: 9999px; overflow: hidden;">
            <div :style="{ height: '100%', background: 'linear-gradient(90deg, #EC4899, #DB2777)', borderRadius: '9999px', width: Math.min(fundedPct, 100) + '%', transition: 'width 0.5s ease' }"></div>
          </div>
          <span style="font-size: 0.6875rem; color: #9CA3AF; font-weight: 500; white-space: nowrap;">{{ fundedPct }}%</span>
        </div>
      </div>

      <!-- Stats -->
      <div style="text-align: right; flex-shrink: 0;">
        <div style="font-size: 1.25rem; font-weight: 700; color: #111827;">${{ formatCurrency(foundation.total_raised_with_children_campaigns) }}</div>
        <div style="font-size: 0.6875rem; color: #9CA3AF;">of ${{ formatCurrency(foundation.goal) }} goal</div>
      </div>

      <!-- Actions -->
      <div style="display: flex; flex-direction: column; gap: 0.25rem; flex-shrink: 0;">
        <a :href="'/edit-campaign/' + foundation.id" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Edit">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </a>
        <a :href="'/sub-campaigns/' + foundation.id" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Manage Supporters">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </a>
        <a :href="'/admin/campaign/' + foundation.id + '/template'" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Template Content">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </a>
        <a v-if="foundation.impact_score" :href="'/admin/reports/impact-leaderboard/' + foundation.id" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Leaderboard">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </a>
        <!-- $event.currentTarget, not the siblings' $event.target.closest('a'):
             closest('a') is null inside a <button> and .style would throw. -->
        <button
          v-if="canArchive"
          @click="toggleArchive"
          :disabled="archiving"
          :title="foundation.is_active ? 'Archive' : 'Un-Archive'"
          :style="{ padding: '0.375rem', color: '#6B7280', background: 'none', border: 'none', borderRadius: '0.25rem', cursor: archiving ? 'default' : 'pointer', lineHeight: 0, opacity: archiving ? 0.4 : 1 }"
          @mouseover="$event.currentTarget.style.color='#EC4899'; $event.currentTarget.style.background='#FDF2F8'"
          @mouseout="$event.currentTarget.style.color='#6B7280'; $event.currentTarget.style.background='transparent'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Expand/collapse toggle -->
    <div
      style="display: flex; align-items: center; justify-content: center; padding: 0.5rem; background: #F9FAFB; border-top: 1px solid #E5E7EB; cursor: pointer; user-select: none;"
      @click="toggleExpanded"
      @mouseover="$event.currentTarget.style.background='#F3F4F6'"
      @mouseout="$event.currentTarget.style.background='#F9FAFB'"
    >
      <svg xmlns="http://www.w3.org/2000/svg" :style="{ width: '0.875rem', height: '0.875rem', color: '#9CA3AF', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
      <span style="font-size: 0.75rem; color: #9CA3AF; margin-left: 0.375rem; font-weight: 500;">
        {{ expanded ? 'Hide' : 'Show' }} top campaigns
      </span>
    </div>

    <!-- Expanded children section -->
    <div v-if="expanded" style="background: #F8FAFC; border-top: 1px solid #E5E7EB;">

      <!-- Search bar (shows when expanded with load-more) -->
      <div v-if="showSearch || searchQuery" style="padding: 0.625rem 1.125rem 0.25rem; display: flex; gap: 0.5rem; align-items: center;">
        <div style="position: relative; flex: 1;">
          <svg xmlns="http://www.w3.org/2000/svg" style="position: absolute; left: 0.625rem; top: 50%; transform: translateY(-50%); width: 0.8125rem; height: 0.8125rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            v-model="searchInput"
            type="text"
            placeholder="Search campaigns..."
            @keyup.enter="searchChildren"
            style="width: 100%; padding: 0.4375rem 0.625rem 0.4375rem 2rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.8125rem;"
          />
        </div>
        <button @click="searchChildren" style="padding: 0.4375rem 0.75rem; background: #374151; color: white; border: none; border-radius: 0.375rem; font-size: 0.8125rem; cursor: pointer; font-weight: 500;">Search</button>
        <a v-if="searchQuery" href="#" @click.prevent="clearSearch" style="color: #9CA3AF; text-decoration: none; font-size: 0.75rem; padding: 0.25rem;">✕</a>
      </div>

      <!-- Section label -->
      <div style="padding: 0.5rem 1.125rem 0.25rem 3.75rem; font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; font-weight: 600; display: flex; justify-content: space-between; align-items: center;">
        <span>Top Campaigns{{ searchQuery ? ' matching "' + searchQuery + '"' : '' }}</span>
        <span v-if="childrenTotal > 0" style="text-transform: none; letter-spacing: normal;">
          {{ children.length }} of {{ childrenTotal }}
        </span>
      </div>

      <!-- Children list -->
      <div v-if="childrenLoading" style="text-align: center; padding: 1.5rem; color: #9CA3AF;">
        <span style="font-size: 0.8125rem;">Loading...</span>
      </div>

      <div v-else-if="children.length === 0" style="text-align: center; padding: 1.5rem; color: #9CA3AF;">
        <span style="font-size: 0.8125rem;">{{ searchQuery ? 'No campaigns match your search' : 'No campaigns yet' }}</span>
      </div>

      <template v-else>
        <div v-for="(child, index) in children" :key="child.id">
          <!-- Child row -->
          <div
            style="display: flex; align-items: center; padding: 0.625rem 1.125rem 0.625rem 3.75rem; gap: 0.625rem;"
            :style="{ borderBottom: (child.children && child.children.length > 0) ? 'none' : (index < children.length - 1 ? '1px solid #EFF1F3' : 'none') }"
            @mouseover="$event.currentTarget.style.background='#EFF6FF'"
            @mouseout="$event.currentTarget.style.background='transparent'"
          >
            <!-- Connector -->
            <div style="flex-shrink: 0; width: 1rem; display: flex; align-items: center; justify-content: center; color: #D1D5DB;">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M0 0 L0 6 L12 6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              </svg>
            </div>

            <!-- Small icon -->
            <div :style="{ flexShrink: 0, width: '1.75rem', height: '1.75rem', borderRadius: '0.5rem', background: (child.children && child.children.length > 0) ? 'linear-gradient(135deg, #FDF2F8, #FCE7F3)' : 'white', border: (child.children && child.children.length > 0) ? '1.5px solid #FBCFE8' : '1.5px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }">
              <svg v-if="child.children && child.children.length > 0" xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem; color: #EC4899;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>

            <!-- Name + location -->
            <div style="flex: 1; min-width: 0;">
              <div style="display: flex; align-items: center; gap: 0.375rem; flex-wrap: wrap;">
                <a :href="'/campaigns/' + child.id" target="_blank" style="font-size: 0.875rem; font-weight: 600; color: #374151; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#374151'">
                  {{ truncate(child.name, 45) }}
                </a>
                <span v-if="child.children && child.children.length > 0" style="font-size: 0.625rem; padding: 0.125rem 0.375rem; background: linear-gradient(135deg, #FDF2F8, #FCE7F3); color: #BE185D; border-radius: 9999px; font-weight: 600; border: 1px solid #FBCFE8;">
                  {{ child.children.length }} sub-team{{ child.children.length > 1 ? 's' : '' }}
                </span>
              </div>
              <div v-if="child.city || child.state || child.business_name" style="font-size: 0.75rem; color: #9CA3AF; margin-top: 0.0625rem;">
                {{ child.business_name ? child.business_name + ' · ' : '' }}{{ child.city }}{{ child.city && child.state ? ', ' : '' }}{{ child.state }}
              </div>
            </div>

            <!-- Mini progress + amount -->
            <div style="text-align: right; flex-shrink: 0; min-width: 80px;">
              <div style="font-size: 0.875rem; font-weight: 600; color: #374151;">${{ formatCurrency(child.total_raised_with_children_campaigns) }}</div>
              <div style="height: 0.25rem; width: 60px; background: #F3F4F6; border-radius: 9999px; overflow: hidden; margin-top: 0.25rem; margin-left: auto;">
                <div :style="{ height: '100%', background: '#EC4899', borderRadius: '9999px', width: Math.min(child.funded || 0, 100) + '%' }"></div>
              </div>
            </div>

            <!-- Quick actions -->
            <div style="display: flex; gap: 0.125rem; flex-shrink: 0;">
              <a :href="'/edit-campaign/' + child.id" style="padding: 0.25rem; color: #C4C9D1; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'" @mouseout="$event.target.closest('a').style.color='#C4C9D1'" title="Edit">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </a>
              <a v-if="child.children && child.children.length > 0" :href="'/sub-campaigns/' + child.id" style="padding: 0.25rem; color: #C4C9D1; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'" @mouseout="$event.target.closest('a').style.color='#C4C9D1'" title="Sub-campaigns">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </a>
            </div>
          </div>

          <!-- ─── GRANDCHILDREN (third level, nested under child) ─── -->
          <div v-if="child.children && child.children.length > 0">
            <div
              v-for="(grandchild, gi) in child.children"
              :key="grandchild.id"
              style="display: flex; align-items: center; padding: 0.4375rem 1.125rem 0.4375rem 6.5rem; gap: 0.5rem;"
              :style="{ borderBottom: (gi < child.children.length - 1) ? '1px solid #F3F4F6' : (index < children.length - 1 ? '1px solid #EFF1F3' : 'none') }"
              @mouseover="$event.currentTarget.style.background='#F0F9FF'"
              @mouseout="$event.currentTarget.style.background='transparent'"
            >
              <!-- Connector -->
              <div style="flex-shrink: 0; width: 0.75rem; display: flex; align-items: center; justify-content: center; color: #E5E7EB;">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M0 0 L0 5 L10 5" stroke="currentColor" stroke-width="1.25" fill="none" stroke-linecap="round"/>
                </svg>
              </div>

              <!-- Tiny icon -->
              <div style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; border-radius: 0.25rem; background: white; border: 1px solid #E5E7EB; display: flex; align-items: center; justify-content: center;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.625rem; height: 0.625rem; color: #C4C9D1;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>

              <!-- Name -->
              <div style="flex: 1; min-width: 0;">
                <a :href="'/campaigns/' + grandchild.id" target="_blank" style="font-size: 0.75rem; font-weight: 500; color: #6B7280; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#6B7280'">
                  {{ truncate(grandchild.name, 35) }}
                </a>
                <span v-if="grandchild.city || grandchild.state" style="font-size: 0.6875rem; color: #C4C9D1; margin-left: 0.25rem;">
                  {{ grandchild.city }}{{ grandchild.city && grandchild.state ? ', ' : '' }}{{ grandchild.state }}
                </span>
              </div>

              <!-- Amount -->
              <div style="font-size: 0.75rem; font-weight: 500; color: #9CA3AF; flex-shrink: 0;">
                ${{ formatCurrency(grandchild.total_raised_with_children_campaigns) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Load more / View all -->
      <div v-if="children.length > 0 && (hasMoreChildren || !searchQuery)" style="display: flex; justify-content: center; gap: 0.75rem; padding: 0.75rem; border-top: 1px solid #EFF1F3;">
        <button
          v-if="hasMoreChildren"
          @click="loadMoreChildren"
          :disabled="childrenLoading"
          style="padding: 0.4375rem 1rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.8125rem; cursor: pointer; font-weight: 500;"
          @mouseover="$event.target.style.borderColor='#EC4899'; $event.target.style.color='#EC4899'"
          @mouseout="$event.target.style.borderColor='#D1D5DB'; $event.target.style.color='#374151'"
        >
          Load More
        </button>
        <a
          :href="'/sub-campaigns/' + foundation.id"
          style="padding: 0.4375rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.8125rem; text-decoration: none; font-weight: 500; display: inline-block;"
          @mouseover="$event.target.style.background='#DB2777'"
          @mouseout="$event.target.style.background='#EC4899'"
        >
          View All {{ foundation.direct_child_count }} Campaigns →
        </a>
      </div>
    </div>
  </div>
</template>

<script>
// Relative, not "@/": vitest.config.js defines an @ alias that webpack.mix.js
// does not, so an @ import passes test:unit and dies at `npm run prod`.
import { buildHttpErrorNotification } from "../../http-error-notifier";

export default {
  props: {
    foundation: { type: Object, required: true },
    // Server-supplied, read from User::canArchiveCampaign(). Deliberately NOT
    // the `admin` flag, which is just hasTeams() and includes outside coaches.
    canArchive: { type: Boolean, default: false },
  },
  data() {
    return {
      expanded: false,
      children: [],
      childrenLoading: false,
      childrenPage: 1,
      childrenTotal: 0,
      childrenLastPage: 1,
      searchInput: '',
      searchQuery: '',
      showSearch: false,
      initialLoaded: false,
      fullLoaded: false,
      archiving: false,
    }
  },
  computed: {
    fundedPct() {
      if (!this.foundation.total_raised_with_children_campaigns || !this.foundation.goal) return 0;
      return Math.round((this.foundation.total_raised_with_children_campaigns / this.foundation.goal) * 100);
    },
    hasMoreChildren() {
      return this.childrenPage < this.childrenLastPage;
    },
  },
  methods: {
    toggleArchive() {
      const archiving = !!this.foundation.is_active;

      // Deliberately no count here. direct_child_count is DIRECT children only,
      // but archiving flips the whole subtree — 817 vs 992 on "2025 Dig Pink
      // Rally" — and a confirmation dialog is the wrong place for a number
      // that is quietly too small. The success toast reports the real count.
      const message = archiving
        ? 'Archive "' + this.foundation.name + '"?\n\n'
          + 'This moves it and all of its sub-campaigns to Past Campaigns. '
          + 'They stop showing on admin dashboards and in the public campaign-search API.\n\n'
          + 'The public campaign page stays live and can still accept donations.\n\n'
          + 'Un-Archive restores exactly the campaigns this action archives.'
        : 'Un-archive "' + this.foundation.name + '"?\n\n'
          + 'This restores it, plus the sub-campaigns that were archived together with it. '
          + 'Sub-campaigns archived separately keep their current status.';

      if (!window.confirm(message)) return;

      const url = '/admin/campaign/' + this.foundation.id + (archiving ? '/archive' : '/unarchive');
      this.archiving = true;

      axios.post(url)
        .then(response => {
          this.$notify({ title: 'Done', type: 'success', text: response.data.message, duration: 5000, group: '' });
          this.$emit('archived', this.foundation.id);
        })
        .catch(error => {
          this.$notify(buildHttpErrorNotification(error));
        })
        .finally(() => {
          this.archiving = false;
        });
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      if (this.expanded && !this.initialLoaded) {
        // Use pre-loaded top children first, then allow loading more
        if (this.foundation.top_children && this.foundation.top_children.length > 0) {
          this.children = this.foundation.top_children;
          this.childrenTotal = this.foundation.direct_child_count;
          this.childrenLastPage = Math.ceil(this.foundation.direct_child_count / 20);
          this.showSearch = this.foundation.direct_child_count > 5;
          this.initialLoaded = true;
        } else {
          this.loadChildren();
        }
      }
    },
    loadChildren(append = false) {
      this.childrenLoading = true;
      const params = { page: this.childrenPage };
      if (this.searchQuery) params.search = this.searchQuery;

      axios.get('/get-foundation-children/' + this.foundation.id, { params }).then(response => {
        const data = response.data;
        if (append) {
          this.children = this.children.concat(data.data);
        } else {
          this.children = data.data;
        }
        this.childrenTotal = data.total;
        this.childrenLastPage = data.last_page;
        this.showSearch = data.total > 5;
        this.childrenLoading = false;
        this.initialLoaded = true;
      }).catch(() => {
        this.childrenLoading = false;
      });
    },
    loadMoreChildren() {
      // If we started with pre-loaded top 5, first "Load More" fetches page 1 from API (replaces preview)
      if (this.childrenPage === 1 && !this.fullLoaded) {
        this.fullLoaded = true;
        this.loadChildren(false);
        return;
      }
      this.childrenPage++;
      this.loadChildren(true);
    },
    searchChildren() {
      this.searchQuery = this.searchInput;
      this.childrenPage = 1;
      this.loadChildren(false);
    },
    clearSearch() {
      this.searchInput = '';
      this.searchQuery = '';
      this.childrenPage = 1;
      // Reset to top children
      if (this.foundation.top_children && this.foundation.top_children.length > 0) {
        this.children = this.foundation.top_children;
        this.childrenTotal = this.foundation.direct_child_count;
        this.childrenLastPage = Math.ceil(this.foundation.direct_child_count / 20);
      } else {
        this.loadChildren(false);
      }
    },
    formatCurrency(dollars) {
      if (!dollars) return '0';
      return Number(dollars).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.substring(0, len) + '…' : str;
    },
  },
}
</script>
